import React, { forwardRef } from "react";

/**
 * iOS Safari only raises the on-screen keyboard when focus() runs inside the tap
 * the user actually made. A field inside a popup is not mounted yet at tap time,
 * and after an await the tap is over, so focusing it later never opens the
 * keyboard.
 *
 * This is an always-mounted, visually hidden but focusable input. Focus it
 * synchronously in the tap handler (primeKeyboard) to raise the keyboard, then
 * let the real field take focus once it mounts - moving focus from one input to
 * another keeps the keyboard up.
 *
 * It must stay genuinely focusable: display:none / visibility:hidden / readOnly
 * / disabled all stop iOS from showing the keyboard.
 */
const primerStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "1px",
  height: "1px",
  padding: 0,
  margin: 0,
  border: 0,
  outline: "none",
  opacity: 0,
  background: "transparent",
  fontSize: "16px", // anything smaller makes iOS zoom the page on focus
  pointerEvents: "none",
  zIndex: -1,
};

export const primeKeyboard = (primerRef) => {
  const input = primerRef?.current;
  if (!input) {
    return;
  }
  try {
    input.focus({ preventScroll: true });
  } catch (error) {
    input.focus();
  }
};

export const releaseKeyboard = (primerRef) => {
  const input = primerRef?.current;
  if (input && document.activeElement === input) {
    input.blur();
  }
};

const KeyboardPrimerInput = forwardRef((props, ref) => <input ref={ref} type="text" inputMode="numeric" tabIndex={-1} aria-hidden="true" autoComplete="off" style={primerStyle} onChange={() => {}} value="" />);

KeyboardPrimerInput.displayName = "KeyboardPrimerInput";

export default KeyboardPrimerInput;
